import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import packageInfo from "../../../package.json";
import { getUser, ClientPrincipal, isAdmin, signInForCurrentPage } from "../../auth/user";
import { usePermissions, Permission } from "../../auth/permissions";

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
  perm?: Permission;
  superOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/admin", label: "Submissions", end: true, perm: "submissions:view" },
  { to: "/admin/columns", label: "Columns", perm: "submissions:view" },
  { to: "/admin/admins", label: "Admins", superOnly: true },
  { to: "/admin/notifications", label: "Notifications", superOnly: true },
  { to: "/admin/checkpoints", label: "Version history", superOnly: true },
  { to: "/admin/database", label: "Database", superOnly: true },
  { to: "/admin/progress-stages", label: "Progress stages", perm: "progressStages:edit" },
  { to: "/admin/form-editor", label: "Edit form", perm: "formEdit:view" }
];

const COLLAPSE_KEY = "admin-sidebar-collapsed";

export default function AdminLayout() {
  const [user, setUser] = useState<ClientPrincipal | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => localStorage.getItem(COLLAPSE_KEY) === "1");
  const perms = usePermissions();
  const location = useLocation();

  useEffect(() => {
    getUser().then((currentUser) => {
      if (!currentUser) {
        signInForCurrentPage();
        return;
      }
      setUser(currentUser);
      setAuthChecked(true);
    });
  }, []);

  useEffect(() => {
    localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  if (!authChecked) {
    return <main className="admin-main">Signing in...</main>;
  }

  if (!isAdmin(user)) {
    return <main className="admin-main">You do not have access to the admin area.</main>;
  }

  const visibleItems = NAV_ITEMS.filter((item) => {
    if (perms.loading) return item.perm === "submissions:view";
    if (item.superOnly) return perms.isSuper;
    if (item.perm) return perms.has(item.perm);
    return true;
  });

  const currentItem =
    [...visibleItems]
      .sort((a, b) => b.to.length - a.to.length)
      .find((item) =>
        item.end ? location.pathname === item.to : location.pathname.startsWith(item.to)
      ) ?? visibleItems.find((i) => i.end);

  return (
    <div className={"admin-page" + (collapsed ? " sidebar-collapsed" : "")}>
      <aside className="admin-sidebar">
        <div className="admin-sidebar-head">
          {!collapsed && <h2>Admin</h2>}
          <button
            type="button"
            className="sidebar-toggle"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand" : "Collapse"}
          >
            {collapsed ? "»" : "«"}
          </button>
        </div>
        {!collapsed && (
          <Link to="/" className="hint admin-back-link">← Form</Link>
        )}
        <nav className="admin-side-nav">
          {visibleItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => "side-nav-item " + (isActive ? "active" : "")}
              title={item.label}
            >
              {collapsed ? item.label.charAt(0) : item.label}
            </NavLink>
          ))}
        </nav>
        {!collapsed && (
          <div className="admin-sidebar-foot hint">
            {user
              ? (() => {
                  const raw = user.userDetails || "";
                  let display = user.displayName?.trim();
                  if (!display) {
                    const name = raw.includes("@") ? raw.split("@")[0].split(/[._]/)[0] : raw;
                    display = name ? name.charAt(0).toUpperCase() + name.slice(1) : "";
                  }
                  return (
                    <>
                      Signed in as <strong title={raw}>{display}</strong>
                      <br />
                      Version {packageInfo.version}
                    </>
                  );
                })()
              : null}
          </div>
        )}
      </aside>
      <main className="admin-main">
        <div className="admin-current-section">{currentItem?.label ?? "Admin"}</div>
        <Outlet />
      </main>
    </div>
  );
}
